"use client";
import Typography from "@mui/material/Typography";
import { t, Trans } from "@lingui/macro";
import Box from "@mui/material/Box";
import React, { useState, useEffect, useRef } from "react";
import { Button } from "@mui/material";
import ChooseNow from "./choose-now";
import CustomSearchInput from "./search";
import QuantitySelector from "@/components/core/quantity-selector";
import price from "@/utils/price";
import { useLingui } from "@lingui/react";
import SelectedProduct from "./selected-product";
import GiftBanner from "./gift-banner";
import { magicConstants } from "@/utils/magic-constants";
import { useGiftStore } from "@/components/context/gift-store";
import { useProductStore } from "@/components/context/product-store";
import { useShallow } from "zustand/react/shallow";

interface ComponentProps {
  isLoadingProduct: boolean;
  lang: string;
}
const GiftForm: React.FC<ComponentProps> = ({ isLoadingProduct, lang }) => {
  const { i18n } = useLingui();
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const {
    product,
    productOption,
    hasPriceOption,
    setHasPriceOption,
    setCustomizeProduct,
    errors,
    setErrorMessage,
    setButtonClicked,
    setPriceDisplay,
    priceDisplay,
  } = useGiftStore();
  const { qty: quantity } = useProductStore(
    useShallow((state) => ({
      qty: state.qty,
      setQty: state.setQty,
    })),
  );
  function goToCustomizeGift() {
    if (product?.id && errors.length === 0) {
      setButtonClicked(true);
      setCustomizeProduct(true);
    } else {
      setShowWarning(true);
    }
  }

  useEffect(() => {
    if (product?.id) {
      setShowWarning(false);

      const priceOption: any = product.offerz_options.buy_options?.find(
        (element: { option_id: string; product_option_id: string }) =>
          element.option_id == magicConstants.priceOptionID,
      );

      if (priceOption) {
        setHasPriceOption(priceOption);
        if (!productOption.find((option) => hasPriceOption.product_option_id === option.key)) {
          setErrorMessage(priceOption.option_id, t(i18n)`Price required!`);
        }
      }
    } else {
      setErrorMessage("product", t(i18n)`Please select a product!`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  useEffect(() => {
    const validQuantity = Number(quantity) || 0;

    const priceOption = hasPriceOption.product_option_id
      ? productOption.find((option) => hasPriceOption.product_option_id === option.key)
      : undefined;

    const price =
      hasPriceOption.product_option_id && priceOption
        ? validQuantity * Number(priceOption.value)
        : product.special === 0
          ? validQuantity * Number(product.price)
          : validQuantity * Number(product.special);

    setPriceDisplay(price);
  }, [quantity, productOption, hasPriceOption]);

  return (
    <div className="mb-2.5 flex text-sm xs:mt-8 md:mt-0 md:w-[80%] lg:mt-0 lg:w-[60%] xl:mt-0">
      <Box sx={{ width: { xs: "100%", lg: "100%", xl: "unset" }, mx: "auto" }}>
        <Box
          sx={{
            display: { xs: "flex", md: "none", lg: "none", xl: "flex" },
            flexDirection: "column",
            width: "100%",
            maxWidth: { xs: "100%", lg: "100%", xl: "500px" },
          }}
        >
          <Typography
            sx={{ fontSize: "16px", fontWeight: 500, pb: "10px", color: "#ff4a33", pt: "20px" }}
          >
            <Trans>NEW</Trans>
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "30px", md: "35px" },
              fontWeight: 600,
              pb: "10px",
              lineHeight: "1.2",
              color: "#1f1f25",
            }}
          >
            <Trans>Design and give away vouchers yourself</Trans>
          </Typography>
          <Typography sx={{ fontSize: "17px", pb: "10px", lineHeight: "1.35", color: "#1f1f25" }}>
            <Trans>
              Design your own voucher with your photo or video. Choose from hundreds of vouchers or
              our Offerz voucher, this one always fits!
            </Trans>
          </Typography>
        </Box>
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            position: "relative",
            top: 0,
            maxWidth: "500px",
            mx: "auto",
          }}
        >
          <GiftBanner />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: { xs: "100%", lg: "100%", xl: "unset" },
          }}
        >
          <Box
            sx={{
              maxWidth: "500px",
              width: "100%",
            }}
          >
            <ChooseNow lang={lang} outsideRef={boxRef} />
            <Box>
              <Typography sx={{ fontSize: "17px", color: "#000", fontWeight: "bold" }}>
                <Trans>Choose your Voucher you want to Gift</Trans>?
              </Typography>
            </Box>
            <CustomSearchInput outsideRef={boxRef} showWarning={showWarning} lang={lang} />
            <SelectedProduct
              showWarning={showWarning}
              setShowWarning={setShowWarning}
              isLoadingProduct={isLoadingProduct}
            />
            {!product?.id && (
              <Box
                sx={{ margin: { xs: "25px 0", md: "40px 0" }, borderTop: "1px solid #d2d2d7" }}
              ></Box>
            )}
            <Box>
              <Typography sx={{ fontSize: "17px", color: "#000", fontWeight: "bold" }}>
                <Trans>How many</Trans>
                {" " + (product?.id ? product.name : t(i18n)`products`) + " "}
                <Trans>do you want to gift</Trans>?
              </Typography>
            </Box>
            <QuantitySelector
              minimum={isNaN(product?.minimum) ? 1 : Number(product?.minimum)}
              maximum={isNaN(product?.maximum) ? 100 : Number(product?.maximum)}
            />
            <Box
              sx={{
                padding: "25px",
                background: "#f2f2f3",
                borderRadius: "8px",
                marginTop: "40px",
                marginBottom: "50px",
                width: "100%",
                display: "block",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  columnGap: "20px",
                  width: "100%",
                  marginBottom: "30px",
                }}
              >
                <Typography sx={{ fontSize: "20px", maxWidth: "180px", fontWeight: "bold" }}>
                  <Trans>Total</Trans>:
                </Typography>
                {product?.id && (
                  <Typography
                    sx={{
                      fontWeight: "500",
                      fontSize: "16px",
                      lineHeight: 1.31,
                    }}
                  >
                    {price(priceDisplay)}
                  </Typography>
                )}
              </Box>
              <Button
                data-testid="customize-gift"
                variant="contained"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  padding: "10px 5px",
                  borderRadius: "4px",
                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.15)",
                  width: "100%",
                }}
                onClick={() => goToCustomizeGift()}
              >
                <Trans>Customize a gift</Trans>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );
};
export default GiftForm;
